import { InfoIcon, TriangleAlertIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@monksflow/monks-ui';
import { i18n } from '@/shared/i18n';
import { useSetTaxonomyListStatus } from '@/entities/taxonomy-list';
import type { ResolvedList } from '@/entities/taxonomy-list';

export interface PublishBlockedListsProps {
  /** Every bound list of the version about to be published, resolved. */
  resolved: ResolvedList[];
  /** The schema being published, named in the audit reason the button writes. */
  schemaName: string;
}

/**
 * What a publish would do to the vocabularies this schema reads, shown BEFORE
 * it is confirmed.
 *
 * Two different messages, and the difference matters. A list with **no
 * published version** blocks the publish outright — a following binding has
 * nowhere to land, and the server refuses. A list with a **newer draft** does
 * not block anything, but the schema will read the published version rather than
 * the newest work, and someone who just edited that list will otherwise assume
 * their edit shipped.
 *
 * The blocked set is derived here rather than read off the server's refusal:
 * every bound list's detail is already fetched to render the grid, so the dialog
 * can disable the button instead of letting someone press it and be told no.
 * The server's 409 is still the authority — it closes the race where a list is
 * unpublished between this render and the write.
 */
export function PublishBlockedLists({ resolved, schemaName }: PublishBlockedListsProps) {
  const { t } = useTranslation('knowledge');

  const blocked = dedupeByListId(resolved.filter((r) => r.unpublished));
  const newerDrafts = dedupeByListId(
    resolved.filter((r) => !r.unpublished && r.newerDraftVersionNumber !== undefined),
  );

  if (blocked.length === 0 && newerDrafts.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      {blocked.length > 0 && (
        <div className="rounded-md border border-border-light bg-surface p-3">
          {/* `alert`, not plain text: focus goes to the reason textarea below,
              so a screen reader would otherwise never reach the one thing that
              explains why the confirm button is dead. */}
          <p role="alert" className="flex items-center gap-1.5 text-xs font-medium text-foreground">
            <TriangleAlertIcon className="size-3.5 shrink-0" aria-hidden />
            {t('taxonomy.publishBlockedLists.cannotPublishYet', { count: blocked.length })}
          </p>
          <ul className="mt-2 flex flex-col gap-1.5">
            {blocked.map((list) => (
              <li key={list.listId} className="flex items-center justify-between gap-2">
                <span className="min-w-0 truncate text-xs text-muted-foreground">
                  {t('taxonomy.publishBlockedLists.listHasNoPublishedVersion', {
                    name: list.listName,
                  })}
                </span>
                {/* Only when the server would actually accept it. The case
                    that reaches here is a binding PINNED to a draft that is no
                    longer the list's head: it blocks the publish, but the server
                    refuses to publish anything but the head, so the button would
                    be a dead end whose only feedback is a failed request. (An
                    archived version is not `unpublished` and never reaches this
                    block at all.) */}
                {list.publishable && list.versionId && (
                  <PublishListButton
                    listId={list.listId}
                    listName={list.listName}
                    versionId={list.versionId}
                    schemaName={schemaName}
                  />
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {newerDrafts.length > 0 && (
        <div className="rounded-md border border-border-light bg-surface p-3">
          <p className="flex items-center gap-1.5 text-xs font-medium text-foreground">
            <InfoIcon className="size-3.5 shrink-0" aria-hidden />
            {t('taxonomy.publishBlockedLists.publishingAgainstPublished', {
              count: newerDrafts.length,
            })}
          </p>
          <ul className="mt-2 flex flex-col gap-1">
            {newerDrafts.map((list) => (
              <li key={list.listId} className="truncate text-xs text-muted-foreground">
                {t('taxonomy.publishBlockedLists.readingVersionNotDraft', {
                  name: list.listName,
                  version: list.versionNumber ?? '?',
                  draft: list.newerDraftVersionNumber ?? '?',
                })}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * One component per list because `useSetTaxonomyListStatus` is parameterised by
 * `(listId, versionId)` — several blocked lists cannot each get a hook in one
 * component body.
 */
function PublishListButton({
  listId,
  listName,
  versionId,
  schemaName,
}: {
  listId: string;
  listName: string;
  versionId: string;
  schemaName: string;
}) {
  const { t } = useTranslation('knowledge');
  const setStatus = useSetTaxonomyListStatus(listId, versionId);

  return (
    <Button
      variant="outline"
      size="mini"
      // Several blocked lists mean several buttons whose visible text is
      // identical; the list name sits in a sibling span with nothing tying them
      // together, so without this they are indistinguishable by name — and
      // pressing the wrong one writes a real audit entry.
      aria-label={t('taxonomy.publishBlockedLists.publishNamedNow', { name: listName })}
      disabled={setStatus.isPending}
      onClick={() =>
        setStatus.mutate({
          status: 'published',
          // The audit trail takes a mandatory justification, and this button is
          // deliberately one click — so the app writes the reason rather than
          // stacking a second prompt on the publish dialog. It names the schema,
          // which is more specific than what someone types under friction.
          reason: i18n.t('knowledge:taxonomy.publishBlockedLists.publishedToUnblock', {
            name: schemaName,
          }),
        })
      }
    >
      {t('taxonomy.publishBlockedLists.publishNow')}
    </Button>
  );
}

/** One row per list: several fields can bind the same one. */
function dedupeByListId(lists: ResolvedList[]): ResolvedList[] {
  const byId = new Map<string, ResolvedList>();
  for (const l of lists) if (!byId.has(l.listId)) byId.set(l.listId, l);
  return Array.from(byId.values());
}
